import { observable, type IObservableValue } from 'mobx';

import { toError } from 'helpers/toError';

import type { ReferenceSource } from './referenceSource';
import { ClientState, DEFAULT_THRESHOLD, ErrorState, LoadingState, ServerState, type ReferenceState } from './referenceState';

/**
 * Internal engine for EditReference. Owns the current {@link ReferenceState}
 * (swapping it as the initial load resolves) and delegates the option-list
 * surface — `options` / `isLoading` / the footer counts / `loadMore` —
 * straight through to it.
 *
 * react-bootstrap-typeahead only fires `onSearch` / `onInputChange` on real
 * typing — never on a selection or a controlled-value change — so the model
 * needs no echo-suppression. The component routes those two events here:
 * `search` for a typed (non-empty) query, `resetToInitial` for an empty input.
 *
 * On top of the per-state list behavior the model owns the **selection
 * display**: `_display` is the labelled item shown for the controlled `value`,
 * resolved from the value itself when it carries a label, else looked up in the
 * loaded set (`findByKey`), else hydrated for a key-only sentinel via
 * `source.resolve`. A selection populates `_display` synchronously so the label
 * survives the round-trip through the parent's `onChange` (no flicker).
 *
 * No React, no decorators: observability is constructor-time `observable.box`,
 * the class is the type (repo MobX convention).
 */
export class ReferenceModel<T> {
  private readonly _state: IObservableValue<ReferenceState<T>>;
  private readonly _display: IObservableValue<T | null> = observable.box(null);
  // The controlled value last seen, so the display can be re-resolved once the
  // initial load settles (the universe it lives in may not exist yet at mount).
  private _value: T | null = null;
  // Key of the value last reconciled by syncValue, to tell a genuine external
  // change from the echo of our own selection.
  private _lastSyncedKey: string | number | null = null;
  private _hasSynced = false;

  constructor(
    private readonly _source: ReferenceSource<T>,
    private readonly _getKey: (item: T) => string | number,
    private readonly _getLabel: (item: T) => string
  ) {
    this._state = observable.box(this._initialState(), { deep: false });
    this._start();
  }

  // ---- delegated option-list surface ----
  get options(): readonly T[] {
    return this._state.get().options;
  }
  get isLoading(): boolean {
    return this._state.get().isLoading;
  }
  get shownCount(): number {
    return this._state.get().shownCount;
  }
  get filteredCount(): number {
    return this._state.get().filteredCount;
  }
  get canLoadMore(): boolean {
    return this._state.get().canLoadMore;
  }
  get emptyLabel(): string {
    return this._state.get().emptyLabel;
  }
  loadMore = (): void => {
    this._state.get().loadMore();
  };

  // ---- typed-query input (from the typeahead) ----

  /** A typed, non-empty query. (Empty inputs come through `resetToInitial`.) */
  search = (query: string): void => {
    this._state.get().onSearch(query);
  };

  /** Empty input (cleared / backspaced) → back to the initial set. */
  resetToInitial = (): void => {
    this._state.get().reset();
  };

  // ---- selection / sentinel hydration ----

  /**
   * The array react-bootstrap-typeahead's `selected` prop wants: the resolved
   * display item when it matches the value's key, else the value itself when it
   * already carries a label, else a synchronous lookup in the loaded set, else
   * the raw value so a stale/off-page selection still renders something.
   */
  selectedFor = (value: T | null): readonly T[] => {
    if (value === null) return [];
    const key = this._getKey(value);
    const display = this._display.get();
    if (display !== null && this._getKey(display) === key) return [display];
    if (this._getLabel(value) !== '') return [value];
    const found = this._state.get().findByKey(key);
    return [found ?? value];
  };

  /**
   * Wire the typeahead's onChange here. Records the chosen item as the display
   * synchronously (so its label survives the parent round-trip) and returns the
   * new value for the caller to forward to its onChange. A selection is not a
   * search and does not reset the list (#842 keeps the LOV as-is on select).
   */
  handleChange = (items: readonly T[]): T | null => {
    const next = items.length === 0 ? null : items[0];
    this._value = next;
    this._display.set(next);
    // Pre-empt the controlled-value echo: syncValue will see this key as
    // already-reconciled and leave the display (and the list) untouched.
    this._lastSyncedKey = next === null ? null : this._getKey(next);
    this._hasSynced = true;
    // Clearing (not selecting) is an empty input → back to the initial set;
    // a real selection keeps the list as-is (#842).
    if (next === null) this._state.get().reset();
    return next;
  };

  /**
   * Call from the component when the controlled `value` changes. A genuine
   * external change resets the list to its initial set (#842) and re-resolves
   * the display label; the echo of our own selection is a no-op.
   */
  syncValue = (value: T | null): void => {
    const key = value === null ? null : this._getKey(value);
    if (this._hasSynced && key === this._lastSyncedKey) {
      this._value = value;
      return;
    }
    this._hasSynced = true;
    this._lastSyncedKey = key;
    this._value = value;
    this._state.get().reset();
    this._resolveDisplay(value);
  };

  // Resolve the labelled item to show for `value`: keep it if it carries a
  // label, look it up in the loaded set, else (key-only sentinel) hydrate it
  // asynchronously via the source's `resolve`.
  private _resolveDisplay(value: T | null): void {
    if (value === null) {
      this._display.set(null);
      return;
    }
    const key = this._getKey(value);
    if (this._getLabel(value) !== '') {
      this._display.set(value);
      return;
    }
    const found = this._state.get().findByKey(key);
    if (found !== null) {
      this._display.set(found);
      return;
    }
    const current = this._display.get();
    if (current !== null && this._getKey(current) === key) return;
    const src = this._source;
    if (src.kind === 'search') {
      // Show an interim stand-in so the field reads as "selected" while
      // the real label resolves.
      this._display.set(src.interimValue(key));
      src.resolve(key).then((item) => {
        if (item !== null && this._getKey(item) === key) {
          this._display.set(item);
        }
      });
    } else {
      this._display.set(null);
    }
  }

  // ---- load lifecycle / state transitions ----

  private _initialState(): ReferenceState<T> {
    return this._source.kind === 'static' ? new ClientState(this._source.items, this._getKey, this._getLabel) : new LoadingState<T>();
  }

  private _start(): void {
    const src = this._source;
    if (src.kind === 'eager') {
      src.load().then(
        (items) => this._settle(new ClientState(items, this._getKey, this._getLabel)),
        (err: unknown) => this._fail(err)
      );
    } else if (src.kind === 'search') {
      const threshold = src.threshold ?? DEFAULT_THRESHOLD;
      src.search('', threshold).then(
        ({ items, total }) =>
          this._settle(
            total <= threshold ? new ClientState(items, this._getKey, this._getLabel) : new ServerState(items, total, src.search, threshold, this._getKey)
          ),
        (err: unknown) => this._fail(err)
      );
    }
  }

  // Replace the loading state with the resolved one, replaying any query typed
  // while loading and re-resolving the display now the loaded set exists.
  private _settle = (next: ReferenceState<T>): void => {
    const prev = this._state.get();
    this._state.set(next);
    if (prev instanceof LoadingState && prev.bufferedQuery !== '') {
      next.onSearch(prev.bufferedQuery);
    }
    this._resolveDisplay(this._value);
  };

  private _fail = (err: unknown): void => {
    this._state.set(new ErrorState<T>(toError(err)));
  };
}
